import type { ResolvedStyle } from './FontResolver';
import { MeasurementEnvironmentError } from './MeasurementEnvironmentError';

export interface FontMetricSet {
  ascentPx: number;   // pixels
  descentPx: number;  // pixels (positive value)
  lineGapPx: number;  // pixels
  unitsPerEm?: number;
}

/**
 * Placeholder FontMetrics service. In a full implementation this would read
 * OS/face metrics or pre-baked metadata and compute a lineHeight multiple or px.
 */
const PT_TO_PX = 96 / 72; // 1pt = 1/72 inch; CSS px assumes 96 DPI

export function getFontMetrics(style: ResolvedStyle): FontMetricSet {
  // TODO: Replace with actual per-font metrics (OS/face tables) for production.
  // For V1 determinism, we compute placeholder metrics proportional to font size.

  // Optional sanity check: if FontFaceSet is available, verify font presence.
  try {
    const fonts: any = (document as any)?.fonts;
    if (fonts && typeof fonts.check === 'function') {
      const ok = fonts.check(`${Math.max(1, Math.round(style.fontSizePt * PT_TO_PX))}px "${style.fontFamily}"`);
      if (!ok) {
        throw new MeasurementEnvironmentError(
          'FONT_NOT_FOUND',
          `Required font '${style.fontFamily}' is not available for measurement`,
          { missingFonts: [style.fontFamily] }
        );
      }
    }
  } catch {
    // Non-fatal in environments without document.fonts; proceed with placeholders
  }

  const emPx = style.fontSizePt * PT_TO_PX;
  const ascentPx = 0.8 * emPx;
  const descentPx = 0.2 * emPx;
  const lineGapPx = 0.0 * emPx;
  return { ascentPx, descentPx, lineGapPx, unitsPerEm: 1 };
}

export function computeLineHeightMultiple(metrics: FontMetricSet, fontSizePx: number): number {
  // Convert absolute px metrics into a unitless multiple relative to the font size.
  const totalPx = metrics.ascentPx + metrics.descentPx + metrics.lineGapPx;
  const multiple = totalPx / Math.max(1, fontSizePx);
  return Math.max(1.1, Math.min(2, multiple || 1.2));
}


